import React, { useEffect, useMemo, useState } from 'react';
import { useMachineOrder, useMachinesInTheLineWidget } from './hooks/useMachinesInTheLineWidget';
import { MachinesInTheLineWidgetContainer } from './MachinesInTheLineWidget.elements';
import ChangeItemOrderPopUp from 'pages/Line/ChangeItemOrderPopUp';
import { useLineInfo } from '../../../providers/useLineInfoProvider';
import { useTranslation } from 'react-i18next';
import { ToastMsg } from 'common/components/Toast';
import { useFleetRouter } from 'common/pages/fleetV2/hooks/useFleetRouter';
import ColumnIcon from 'icons/indexed-icons/ColumnIcon';
import DocumentationText from 'icons/indexed-icons/DocumentationText';
import DataRenderer from 'components/machine-health/DataRenderer';
import { MachineCardWidget } from '../MachineCardWidget';
import { WidgetUi } from 'common/ui';
import CreateMachineModal from '../CreateMachineModal/CreateMachineModal';

export interface MachineData {
  machineName: string;
  machineLink: string;
  machineDescription?: string;
  MachineTags?: {
    tagName: string;
    targetValue: string;
    actualValue: string;
  }[];
}

const newMachineData = {
  machineName: '',
  machineLink: '',
  machineDescription: '',
  MachineTags: [
    {
      tagName: '',
      targetValue: '',
      actualValue: ''
    },
    {
      tagName: '',
      targetValue: '',
      actualValue: ''
    },
    {
      tagName: '',
      targetValue: '',
      actualValue: ''
    }
  ]
};

export const MachinesInTheLineWidget = (): JSX.Element => {
  const { t } = useTranslation(['mh']);
  const [{ fleetPageVersionPath }, handle] = useFleetRouter();

  const [isChangeOrder, setIsChangeOrder] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [machineData, setMachineData] = useState<MachineData>(newMachineData);
  const [machineList, setMachineList] = useState<{ id: string; name: string }[] | undefined>(undefined);
  const [initialMachineList, setInitialMachineList] = useState<{ id: string; name: string }[] | undefined>(undefined);

  const { lineData, lineId, plantData, isFetchingPlantData } = useLineInfo();
  const { dataLineOrder, dataLineOrderIsFetching } = useMachinesInTheLineWidget({lineId});
  const { createMachineDisplayOrderFunc, updateMachineDisplayOrderFunc } = useMachineOrder();

  const onCancelReOrder = () => {
    setMachineList(initialMachineList);
    setIsChangeOrder(!isChangeOrder);
  };

  const saveChangeOrder = () => {
    const updateMachineDisplayOrderPromisesAll =
      machineList &&
      machineList.map((element, i) => {
        return updateMachineDisplayOrderFunc(element.id, String(i + 1));
      });

    Promise.all(updateMachineDisplayOrderPromisesAll || [])
      .then(() => {
        setInitialMachineList(machineList);
        setIsChangeOrder(!isChangeOrder);
        return ToastMsg({
          message: t('machine_order_change_success_msg', { ns: 'common' }) as string,
          theme: 'colored',
          type: 'success'
        });
      })
      .catch(() => {
        setMachineList(initialMachineList);
        setIsChangeOrder(!isChangeOrder);
        ToastMsg({
          message: t('machine_order_change_error_msg', { ns: 'common' }) as string,
          theme: 'colored',
          type: 'warning'
        });
      });
  };

  const handleClick = (id: string) => {
    handle(`setPath`, {
      path: `/${fleetPageVersionPath}/${id}/machine-health/overview`
    });
  };

  const machines = plantData?.machines.filter((item) => item.lineId === lineId);

  useEffect(() => {
    if (machines && machines.length > 0) {
      const sortedMachineOrder = Array.isArray(dataLineOrder) && dataLineOrder.length
        ? [...dataLineOrder].sort((a, b) => a?.displayOrder - b?.displayOrder)
        : [];

      const updateMachineDisplayOrderWrapper = (machineId: string, order: string) => {
        if (dataLineOrder && dataLineOrder.find((machine) => machine.machineId === machineId)) {
          updateMachineDisplayOrderFunc(machineId, order);
        } else {
          createMachineDisplayOrderFunc(lineId, machineId, order);
        }
      };

      const createMachineList = (order: MachineDisplayOrderItemProps[]) =>
        order.map((item: { machineId: string }) => ({
          id: machines.find((machine) => machine.id === item.machineId)?.id || '',
          name: machines.find((machine) => machine.id === item.machineId)?.description || ''
        }));

      if (machines.length === sortedMachineOrder.length) {
        setMachineList(createMachineList(sortedMachineOrder));
        setInitialMachineList(createMachineList(sortedMachineOrder));
      } else {
        setMachineList(machines.map((item) => ({ id: item.id, name: item.description })));
        setInitialMachineList(machines.map((item) => ({ id: item.id, name: item.description })));

        machines.forEach((element, i) => {
          updateMachineDisplayOrderWrapper(element.id, String(i + 1));
        });
      }
    }
  }, [plantData, dataLineOrder]);

  const Main = useMemo(() => {
    if (import.meta.env.VITE_LINE_VIEW_FEATURE !== 'true') return null;

    const machineMap = new Map(machines?.map(m => [m.id, m]) || []);

    return (
      <DataRenderer isLoading={dataLineOrderIsFetching && isFetchingPlantData}>
      <div className="machines-container">
        {machineList?.map((el) => {
          const machine = machineMap.get(el.id);
          return (
            <MachineCardWidget key={el.id} machineId={el.id} name={machine?.description || ''} handleClick={handleClick} />
          );
        })}
      </div>
      </DataRenderer>
    );
  }, [isChangeOrder, machineList, dataLineOrderIsFetching, isFetchingPlantData, lineData, machines]);

  const widgetSettings = {
    title: t('Line View', { ns: 'common' }) as string,
    hasButtons: {
      headerRight: ['menu']
    },
    customDropdownItems: [
      {
        title: t('Custom widget', { ns: 'common' }) as string,
        onClick: () => setIsModalOpen(true),
        icon: <DocumentationText />
      },
      {
        title: t('Edit layout', { ns: 'common' }) as string,
        onClick: () => setIsChangeOrder(!isChangeOrder),
        icon: <ColumnIcon />
      }
    ]
  };

  const closeModal = () => {
    setMachineData(newMachineData);
    setIsModalOpen(false);
  };

  const handleMachineDataChange = (field: string, value: string) => {
    setMachineData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTagChange = (index: number, field: string, value: string) => {
    setMachineData((prev) => {
      const updatedTags = [...(prev.MachineTags || [])];
      updatedTags[index] = {
        ...updatedTags[index],
        [field]: value
      };
      return {
        ...prev,
        MachineTags: updatedTags
      };
    });
  };

  const handleFileChange = (file: File | null) => {
    setUploadedFile(file);
  };

  const uploadImage = async () => {
    if (!uploadedFile) {
      console.error('No file to upload');
      return;
    }
    console.log('File ready for upload:', uploadedFile);
  };

  // Function to handle the apply button click in the modal
  const onSubmitBtn = async (): Promise<void> => {
    console.log('Submitting with machine data:', machineData);
  };

  return (
    <>
      <MachinesInTheLineWidgetContainer className='machines-in-the-line-widget'>
        <div className='header-btn-container'>
          <WidgetUi {...widgetSettings} />
        </div>
        {Main}
        {isChangeOrder && (
          <ChangeItemOrderPopUp
            machineList={machineList as { id: string; name: string }[]}
            setMachineList={setMachineList}
            isChangeItemOrderPopupOpen={isChangeOrder}
            onCancelReOrder={onCancelReOrder}
            onSaveReOrder={saveChangeOrder}
            isLoading={dataLineOrderIsFetching && isFetchingPlantData}
          />
        )}
        {isModalOpen && (
          <CreateMachineModal
            isOpen={isModalOpen}
            onRequestClose={closeModal}
            onSubmitBtn={onSubmitBtn}
            machineData={machineData}
            handleMachineDataChange={handleMachineDataChange}
            handleTagChange={handleTagChange}
            handleFileChange={handleFileChange}
            uploadImage={uploadImage}
          />
        )}
      </MachinesInTheLineWidgetContainer>
    </>
  )
};
